import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Tag, Users, Headphones, ArrowRight, Globe } from 'lucide-react';
import SellerVerification from './components/SellerVerification';
import Navbar from './components/ui/navbar';

const SellerPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const showVerification = true;

  useEffect(() => {
    // Simulate component loading time
    const timer = setTimeout(() => {
      setLoading(false);
    }, 300); // Short loading time for seller page

    return () => clearTimeout(timer);
  }, []);

  const navbarConfig = {
    logo: {
      url: "/",
      src: "",
      alt: "Fleait logo",
      title: "Fleait"
    },
    menu: [
      { title: "Home", url: "/" },
      { title: "Products", url: "/products" },
      {
        title: "Categories",
        url: "#",
        items: [
          {
            title: "Fashion & Accessories",
            description: "Luxury bags, watches, jewelry, and fashion items",
            icon: <ShoppingBag className="size-5 shrink-0" />,
            url: "#",
          },
          {
            title: "Premium Brands",
            description: "Curated selection of high-end designer brands",
            icon: <Tag className="size-5 shrink-0" />,
            url: "#",
          },
        ],
      },
      {
        title: "Services",
        url: "#",
        items: [
          {
            title: "Personal Shopping",
            description: "Get personalized recommendations from our experts",
            icon: <Users className="size-5 shrink-0" />,
            url: "#",
          },
          {
            title: "Customer Support",
            description: "24/7 support for all your luxury shopping needs",
            icon: <Headphones className="size-5 shrink-0" />,
            url: "#",
          },
        ],
      },
      {
        title: "About",
        url: "/about",
      },
      {
        title: "Contact",
        url: "#",
      },
    ],
    mobileExtraLinks: [
      { name: "Gift Cards", url: "#" },
      { name: "Size Guide", url: "#" },
      { name: "Returns", url: "#" },
      { name: "Shipping", url: "#" },
    ],
    auth: {
      login: { text: "Sign In", url: "/signin" },
      signup: { text: "Create Space", url: "#" },
    },
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent shadow-lg shadow-cyan-500/50"></div>
      </div>
    );
  }

  // Show verification flow
  if (showVerification) {
    return <SellerVerification />;
  }

  // Main landing page with split view
  return (
    <div className="h-screen bg-gray-900 text-white overflow-hidden">
      {/* Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-4 px-4 pointer-events-none">
        <div className="max-w-7xl w-full bg-gray-800/90 backdrop-blur-md border-2 border-gray-700 rounded-[40px] px-8 pointer-events-auto transition-all duration-500 hover:scale-[1.01] shadow-xl">
          <Navbar {...navbarConfig} />
        </div>
      </div>

      {/* Split View Container */}
      <div className="h-full pt-24 pb-6 px-6 flex items-center justify-center">
        <div className="w-full max-w-7xl h-full flex gap-6">

          {/* Left Card - Seller Benefits */}
          <div className="flex-1 bg-gradient-to-br from-gray-800/90 via-purple-900/40 to-gray-800/90 backdrop-blur-md border-2 border-gray-700 rounded-3xl p-6 flex flex-col justify-between shadow-2xl hover:border-cyan-500/50 transition-all duration-300 overflow-hidden">
            <div className="flex-1 flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-purple-600 text-white px-3 py-1.5 rounded-full mb-4 shadow-lg shadow-cyan-500/50 w-fit">
                <Globe className="w-3.5 h-3.5" />
                <span className="font-bold text-xs">Global Platform</span>
              </div>

              <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent leading-tight">
                Sell Globally with Fleait
              </h1>

              <p className="text-base text-gray-300 mb-6">
                Join thousands of sellers earning more with our <span className="text-cyan-400 font-bold">3-5% commission</span> platform. Unlike competitors charging 15-30%, we help you keep what you earn.
              </p>

              {/* Commission Comparison */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-gradient-to-br from-green-500/20 to-cyan-500/20 border-2 border-cyan-500 rounded-2xl p-3 shadow-lg shadow-cyan-500/20">
                  <h3 className="text-sm font-bold text-cyan-400 mb-0.5">Fleait Commission</h3>
                  <p className="text-2xl font-bold text-white">3-5%</p>
                  <p className="text-gray-300 text-xs">You keep 95-97% of earnings</p>
                </div>

                <div className="bg-gradient-to-br from-red-500/20 to-orange-500/20 border-2 border-red-500 rounded-2xl p-3 shadow-lg shadow-red-500/20">
                  <h3 className="text-sm font-bold text-red-400 mb-0.5">Other Platforms</h3>
                  <p className="text-2xl font-bold text-white">15-30%</p>
                  <p className="text-gray-300 text-xs">You lose up to 30% in fees</p>
                </div>
              </div>

              {/* Features */}
              <div className="grid grid-cols-3 gap-2 mb-6">
                <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-3 hover:border-cyan-500 transition-all text-center">
                  <div className="text-xl mb-1">✓</div>
                  <h3 className="text-xs font-bold mb-0.5">Easy Verification</h3>
                  <p className="text-gray-400 text-[10px]">Simple 3-step process</p>
                </div>

                <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-3 hover:border-purple-500 transition-all text-center">
                  <div className="text-xl mb-1">🌍</div>
                  <h3 className="text-xs font-bold mb-0.5">Global Reach</h3>
                  <p className="text-gray-400 text-[10px]">Sell to customers worldwide</p>
                </div>

                <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-3 hover:border-pink-500 transition-all text-center">
                  <div className="text-xl mb-1">💰</div>
                  <h3 className="text-xs font-bold mb-0.5">Fast Payouts</h3>
                  <p className="text-gray-400 text-[10px]">PayPal or bank transfer</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate('/create-space')}
                className="w-full px-5 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-bold text-base hover:from-cyan-600 hover:to-purple-700 transition-all shadow-lg shadow-cyan-500/50 flex items-center justify-center gap-2 group"
              >
                Start Selling Now
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={() => navigate('/signin')}
                className="w-full px-5 py-3 bg-gray-800 text-white rounded-xl font-bold text-base hover:bg-gray-700 transition-all border-2 border-gray-600 hover:border-cyan-500"
              >
                Existing Seller Login
              </button>
            </div>
          </div>

          {/* Right Card - Image/Visual */}
          <div className="flex-1 bg-gray-800/50 backdrop-blur-md border-2 border-gray-700 rounded-3xl overflow-hidden shadow-2xl hover:border-purple-500/50 transition-all duration-300 relative">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: 'url(/sellerlogin.jpg)',
                filter: 'brightness(0.5)',
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 via-purple-600/30 to-pink-500/20" />

            {/* Overlay Content */}
            <div className="relative z-10 h-full flex flex-col items-center justify-center p-6">
              <div className="bg-gray-900/80 backdrop-blur-md border-2 border-cyan-500/50 rounded-2xl p-6 max-w-md w-full">
                <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent text-center">
                  Why Choose Fleait?
                </h2>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 bg-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">✓</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-sm mb-0.5">Lowest Commission</h3>
                      <p className="text-gray-300 text-xs">Only 3-5% vs industry's 15-30%</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">✓</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-sm mb-0.5">Instant Verification</h3>
                      <p className="text-gray-300 text-xs">Get verified in minutes, not days</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 bg-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">✓</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-sm mb-0.5">Global Marketplace</h3>
                      <p className="text-gray-300 text-xs">Reach millions of buyers worldwide</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">✓</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-sm mb-0.5">Secure & Reliable</h3>
                      <p className="text-gray-300 text-xs">Protected transactions & fast payouts</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SellerPage;
